import { useReducer, useEffect, useCallback, useRef } from 'react';
import type { GenUIEvent } from '../core/events';
import { genUIReducer, initialState, type GenUIState, type GenUIAction } from '../core/reducer';
import type { GenUIResponse } from '../core/parser';
import { normalizeStream, normalizeResponse, type StreamInput } from '../streaming/normalize';

interface UseGenUIOptions {
    /** Static response (non-streaming) */
    response?: GenUIResponse;
    /** Streaming input (EventSource, ReadableStream, or Response) */
    stream?: StreamInput;
    /** Callback when streaming completes */
    onComplete?: () => void;
    /** Callback on error */
    onError?: (error: Error) => void;
}

interface UseGenUIResult {
    /** Current state */
    state: GenUIState;
    /** Reset the state */
    reset: () => void;
    /** Manually dispatch an event */
    dispatch: (action: GenUIAction) => void;
}

/**
 * Hook to manage GenUI streaming state
 */
export function useGenUI(options: UseGenUIOptions): UseGenUIResult {
    const { response, stream, onComplete, onError } = options;
    const [state, dispatch] = useReducer(genUIReducer, initialState);
    const abortRef = useRef(false);

    // Process static response
    useEffect(() => {
        if (!response) return;

        dispatch({ type: 'RESET' });
        dispatch({ type: 'START_STREAMING' });

        for (const event of normalizeResponse(response)) {
            dispatch({ type: 'EVENT', event });
        }
    }, [response]);

    // Process streaming input
    useEffect(() => {
        if (!stream) return;

        abortRef.current = false;
        dispatch({ type: 'RESET' });
        dispatch({ type: 'START_STREAMING' });

        const processStream = async () => {
            try {
                for await (const event of normalizeStream(stream)) {
                    if (abortRef.current) break;
                    dispatch({ type: 'EVENT', event });
                }
                onComplete?.();
            } catch (error) {
                const err = error instanceof Error ? error : new Error(String(error));
                dispatch({ type: 'SET_ERROR', error: err });
                onError?.(err);
            }
        };

        processStream();

        return () => {
            abortRef.current = true;
        };
    }, [stream, onComplete, onError]);

    const reset = useCallback(() => {
        dispatch({ type: 'RESET' });
    }, []);

    return {
        state,
        reset,
        dispatch,
    };
}
